//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React from 'react';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LanIcon from '@mui/icons-material/Lan';

// Why this is its own module: the Getting Started tour imports these, and
// importing them from DemoDataLoader instead closes a cycle back through
// routes/index.tsx -> HelpManager -> OnboardingTourStarter, which calls a
// function from the tour module at its own top level -- as a load-time
// throw rather than an assertion failure. Nothing here may import a hook.

interface DemoDataEntry {
  label: string;
  icon: React.ReactNode;
  rowCount: number;
  description: React.ReactNode;
}

/**
 * A row count in millions, rounded to two decimal places below a million.
 * Below 5,000 rows this still floors to 0 -- no demo dataset is that small,
 * so the two decimal places exist to distinguish datasets in the hundreds
 * of thousands, not to keep a near-empty one from reading as zero.
 */
export const roundedMillions = (rows: number): number =>
  rows >= 1_000_000 ? Math.round(rows / 1_000_000) :
                      Math.round(rows / 10_000) / 100;

const FINTRANS_ROW_COUNT = 2_101_709;
const NETFLOW_ROW_COUNT = 6_644_483;

// The list of demo datasets.  The key is the dataset name.
export const demoData: Record<string, DemoDataEntry> = {
  fintrans: {
    label: 'Financial Transactions',
    icon: <AttachMoneyIcon />,
    rowCount: FINTRANS_ROW_COUNT,
    description:
      <>
        <strong>Synthetic AML Transactions</strong><br />
        ~{roundedMillions(FINTRANS_ROW_COUNT)}M rows including
        sender/receiver accounts, timestamps, currencies, and payment
        types.<br />
        Fully synthetic data generated from a virtual financial system. No
        real individuals or institutions are represented.
      </>
    ,
  },
  netflow: {
    label: 'Netflow',
    icon: <LanIcon />,
    rowCount: NETFLOW_ROW_COUNT,
    description:
      <>
        <strong>LANL Netflow Dataset</strong><br />
        ~{roundedMillions(NETFLOW_ROW_COUNT)}M rows of deidentified internal
        network flow logs collected over ~90 days from LANL Windows hosts
        and routers. Each record includes source/destination devices,
        protocol, ports, packet/byte counts, and timestamps. Identities are
        anonymized but consistent across records for analysis.
      </>
    ,
  },
};

// "a or b" at two datasets, "a, b, or c" at three; the punctuation of the
// list is the platform's rather than this module's.
const DEMO_ROW_LIST = new Intl.ListFormat(undefined, {
  style: 'long', type: 'disjunction'
});

/**
 * What the demo datasets cost, for copy that has to say so before loading
 * one. Derived from `demoData` so the two cannot disagree. A fragment: the
 * caller supplies the word "rows".
 */
export const demoRowSummary = (): string => {
  const parts = Object.values(demoData).map(
    ({ label, rowCount }) => `${roundedMillions(rowCount)} million (${label})`);
  return DEMO_ROW_LIST.format(parts);
};
